import { inject } from "@angular/core";
import { patchState, signalStoreFeature, type, withMethods } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { tapResponse } from "@ngrx/operators";
import { IProviderState } from "./provider.state";
import { exhaustMap, pipe, tap } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { ProviderService } from "../../services/provider/provider.service";
import { Provider } from "../../models/provider/provider.model";
import { withProviderStateMethods } from "./provider.methods.state";
import { NotificationService } from "../../services/notification/notification.service";
import { ProviderProfile } from "../../models/provider/provider-profile.model";

export const withProviderApiMethods = signalStoreFeature(
    { state: type<IProviderState>() },
    withProviderStateMethods,
    withMethods((store) => {
        const providerService = inject(ProviderService);
        const notificationService = inject(NotificationService);

        const loadProvider = rxMethod<string>(
            pipe(
                tap((providerGuid) => patchState(store, { loading: true, error: null, providerGuid })),
                exhaustMap((providerGuid) =>
                    providerService.getByGuid(providerGuid).pipe(
                        tapResponse({
                            next: (provider: Provider) => store.setProvider(provider),
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        const loadProviderByPersonGuid = rxMethod<string>(
            pipe(
                tap((personGuid) => patchState(store, { loading: true, error: null })),
                exhaustMap((personGuid) =>
                    providerService.getByPersonGuid(personGuid).pipe(
                        tapResponse({
                            next: (provider: Provider) => store.setProvider(provider),
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        const createProvider = rxMethod<{ payload: Partial<Provider> }>(
            pipe(
                tap(() => patchState(store, { saving: true, error: null })),
                exhaustMap(({ payload }) =>
                    providerService.create(payload).pipe(
                        tapResponse({
                            next: (provider: Provider) => {
                                store.setProvider(provider);
                                notificationService.notify('success', 'Success', 'Provider created successfully.');
                            },
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        const updateProvider = rxMethod<{ providerGuid: string; payload: Partial<Provider> }>(
            pipe(
                tap(() => patchState(store, { saving: true, error: null })),
                exhaustMap(({ providerGuid, payload }) =>
                    providerService.update(providerGuid, payload).pipe(
                        tapResponse({
                            next: (provider: Provider) => {
                                store.setProvider(provider);
                                notificationService.notify('success', 'Success', 'Provider updated successfully.');
                            },
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        const loadProviderProfile = rxMethod<string>(
            pipe(
                tap((providerGuid) => patchState(store, { loading: true, error: null, providerGuid })),
                exhaustMap((providerGuid) =>
                    providerService.getProfile(providerGuid).pipe(
                        tapResponse({
                            next: (providerProfile: ProviderProfile) => store.setProviderProfile(providerProfile),
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        const updateProviderProfile = rxMethod<{ providerGuid: string; payload: Partial<ProviderProfile> }>(
            pipe(
                tap(() => patchState(store, { saving: true, error: null })),
                exhaustMap(({ providerGuid, payload }) =>
                    providerService.updateProfile(providerGuid, payload).pipe(
                        tapResponse({
                            next: (provider: Provider) => {
                                store.setProvider(provider);
                                notificationService.notify('success', 'Success', 'Provider updated successfully.');
                            },
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        const deleteProvider = rxMethod<string>(
            pipe(
                tap(() => patchState(store, { deleting: true, error: null })),
                exhaustMap((providerGuid) =>
                    providerService.delete(providerGuid).pipe(
                        tapResponse({
                            next: () => {
                                store.setProvider(null);
                                notificationService.notify('success', 'Success', 'Provider deleted successfully.');
                            },
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        const verifyProvider = rxMethod<string>(
            pipe(
                exhaustMap((providerGuid) =>
                    providerService.verify(providerGuid).pipe(
                        tapResponse({
                            next: (provider: Provider) => {
                                store.setProvider(provider);
                                notificationService.notify('success', 'Success', 'Provider verified successfully.');
                            },
                            error: (error: HttpErrorResponse) => {
                                store.setError(error.message);
                                notificationService.notifyError("Error", error.message);
                            }
                        })
                    )
                )
            )
        );

        return { loadProvider, 
            loadProviderByPersonGuid, 
            createProvider, 
            updateProvider, 
            loadProviderProfile,
            updateProviderProfile, 
            deleteProvider, 
            verifyProvider };
    })
);
